import { FBXReader, FBXReaderNode } from 'fbx-parser';
import { Entity, GltfExporter, Mesh, MeshInstance, Quat, StandardMaterial, Vec3 } from 'playcanvas';

import { readAxisConverter, readId, readNumericArray } from './fbx-parse-utils';
import { Scene } from './scene';

// find ids of objects connected as children of `parentId` whose FBX node type is `nodeType`
// (e.g. "Geometry", "Material") - mirrors the OP-connection lookup pattern already used in
// fbx-camera-import.ts, but for the simpler "OO" (object-object) connections geometry and
// material use.
const findConnectedIds = (
    parentId: number,
    nodesById: Map<number, FBXReaderNode>,
    connections: FBXReaderNode[],
    nodeType: string
): number[] => {
    const result: number[] = [];
    for (const c of connections) {
        const childId = c.prop(1, 'bigint') ?? c.prop(1, 'number');
        const parentIdProp = c.prop(2, 'bigint') ?? c.prop(2, 'number');
        if (parentIdProp === undefined || Number(parentIdProp) !== parentId) continue;
        const child = nodesById.get(Number(childId));
        if (child && child.fbxNode.name === nodeType) {
            result.push(Number(childId));
        }
    }
    return result;
};

type Polygon = { cpIndices: number[], cornerStart: number };

// decode PolygonVertexIndex's flat array (last index of each polygon is -(index+1)) into
// per-polygon control-point-index lists, tracking each polygon's starting "corner index"
// (position in the original flat array) for ByPolygonVertex-mapped layers.
const decodePolygons = (polyVertexIndex: number[]): Polygon[] => {
    const polygons: Polygon[] = [];
    let current: number[] = [];
    let cornerStart = 0;
    let cursor = 0;
    for (const v of polyVertexIndex) {
        if (v < 0) {
            current.push(-v - 1);
            polygons.push({ cpIndices: current, cornerStart });
            cornerStart = cursor + 1;
            current = [];
        } else {
            current.push(v);
        }
        cursor++;
    }
    return polygons;
};

type Layer = { mapping: 'vertice' | 'polygonVertex', direct: number[] };

// read a LayerElementNormal/LayerElementUV node. Only ReferenceInformationType "Direct" is
// supported (v1 scope, see plan Global Constraints) - IndexToDirect layers are treated as
// absent, triggering the caller's fallback.
const readLayer = (layerNode: FBXReaderNode | undefined, dataNodeName: string): Layer | null => {
    if (!layerNode) return null;
    const mappingType = layerNode.node('MappingInformationType')?.prop(0, 'string');
    const referenceType = layerNode.node('ReferenceInformationType')?.prop(0, 'string');
    if (referenceType !== 'Direct') return null;
    const direct = readNumericArray(layerNode.node(dataNodeName));
    if (!direct) return null;
    if (mappingType === 'ByVertice') return { mapping: 'vertice', direct };
    if (mappingType === 'ByPolygonVertex') return { mapping: 'polygonVertex', direct };
    return null;
};

const componentAt = (layer: Layer, cpIndex: number, cornerIndex: number, size: number): number[] => {
    const idx = layer.mapping === 'vertice' ? cpIndex : cornerIndex;
    return layer.direct.slice(idx * size, idx * size + size);
};

// parse one Geometry node's Vertices/PolygonVertexIndex/LayerElementNormal/LayerElementUV
// into a real pc.Mesh. Always builds non-indexed (per-triangle-corner) geometry - simpler
// and correct for both "ByVertice" and "ByPolygonVertex" mappings, at the cost of not
// welding shared vertices (fine for prop-scale meshes, see plan Global Constraints).
const buildMeshFromGeometry = (
    device: unknown,
    geomNode: FBXReaderNode,
    convertAxis: (v: Vec3) => Vec3
): Mesh | null => {
    const rawPositions = readNumericArray(geomNode.node('Vertices'));
    const polyVertexIndex = readNumericArray(geomNode.node('PolygonVertexIndex'));
    if (!rawPositions || !polyVertexIndex) return null;

    const controlPoints: Vec3[] = [];
    for (let i = 0; i + 2 < rawPositions.length; i += 3) {
        controlPoints.push(convertAxis(new Vec3(rawPositions[i], rawPositions[i + 1], rawPositions[i + 2])));
    }

    const normalLayer = readLayer(geomNode.node('LayerElementNormal'), 'Normals');
    const uvLayer = readLayer(geomNode.node('LayerElementUV'), 'UV');

    const polygons = decodePolygons(polyVertexIndex);

    const outPositions: number[] = [];
    const outNormals: number[] = [];
    const outUvs: number[] = [];

    polygons.forEach((polygon) => {
        const { cpIndices, cornerStart } = polygon;
        if (cpIndices.length < 3) return;

        // fan-triangulate the polygon
        for (let i = 1; i < cpIndices.length - 1; i++) {
            const corners = [0, i, i + 1];
            const triCp = corners.map(c => cpIndices[c]);
            const triCorner = corners.map(c => cornerStart + c);

            const p0 = controlPoints[triCp[0]];
            const p1 = controlPoints[triCp[1]];
            const p2 = controlPoints[triCp[2]];
            if (!p0 || !p1 || !p2) continue;
            outPositions.push(p0.x, p0.y, p0.z, p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);

            if (normalLayer) {
                for (let k = 0; k < 3; k++) {
                    const n = componentAt(normalLayer, triCp[k], triCorner[k], 3);
                    const converted = convertAxis(new Vec3(n[0] ?? 0, n[1] ?? 1, n[2] ?? 0));
                    outNormals.push(converted.x, converted.y, converted.z);
                }
            } else {
                // flat face-normal fallback when the FBX has no normal layer
                const e1 = p1.clone().sub(p0);
                const e2 = p2.clone().sub(p0);
                const n = new Vec3().cross(e1, e2).normalize();
                outNormals.push(n.x, n.y, n.z, n.x, n.y, n.z, n.x, n.y, n.z);
            }

            if (uvLayer) {
                for (let k = 0; k < 3; k++) {
                    const uv = componentAt(uvLayer, triCp[k], triCorner[k], 2);
                    outUvs.push(uv[0] ?? 0, uv[1] ?? 0);
                }
            } else {
                outUvs.push(0, 0, 0, 0, 0, 0);
            }
        }
    });

    if (outPositions.length === 0) return null;

    const mesh = new Mesh(device as any);
    mesh.setPositions(outPositions);
    mesh.setNormals(outNormals);
    mesh.setUvs(0, outUvs);
    // PlayCanvas's GltfExporter unconditionally reads mesh.indexBuffer[0].byteLength when
    // collecting buffers (see gltf-exporter.js's getResources()), which throws on a fully
    // non-indexed mesh (indexBuffer[0] is null). Supply a trivial identity index buffer -
    // a no-op given our data is already per-triangle-corner and never welded - so the
    // GltfExporter round-trip this module depends on doesn't crash.
    const vertexCount = outPositions.length / 3;
    const indices = new Uint32Array(vertexCount);
    for (let i = 0; i < vertexCount; i++) indices[i] = i;
    mesh.setIndices(indices);
    mesh.update();
    return mesh;
};

// Parses an FBX file's mesh geometry (if any) and imports it exactly like a dropped .glb:
// build real pc.Mesh/Entity objects in a detached scratch graph, round-trip them through
// PlayCanvas's own GltfExporter to an in-memory GLB, then hand that to the existing
// AssetLoader.loadModel() pipeline. This reuses 100% of Model's .ssproj round-trip,
// undo/redo, and scene wiring - no new "Model-equivalent" class needed.
// Returns false (not an error) if the FBX has no Geometry nodes, so the caller can still
// try camera-animation import on the same file.
const loadFbxMesh = async (reader: FBXReader, scene: Scene, filename: string): Promise<boolean> => {
    const convertAxis = readAxisConverter(reader);

    const objects = reader.node('Objects');
    const geometryNodes = objects?.nodes('Geometry') ?? [];
    if (geometryNodes.length === 0) {
        return false;
    }

    const modelNodes = objects?.nodes('Model') ?? [];
    const materialNodes = objects?.nodes('Material') ?? [];
    const nodesById = new Map<number, FBXReaderNode>();
    [...geometryNodes, ...modelNodes, ...materialNodes].forEach(n => nodesById.set(readId(n), n));

    const connections = reader.node('Connections')?.nodes('C') ?? [];

    const device = scene.app.graphicsDevice;
    const scratchRoot = new Entity('fbxMeshImportScratch');
    let meshCount = 0;

    modelNodes.forEach((modelNode, modelIndex) => {
        const modelId = readId(modelNode);
        const geomIds = findConnectedIds(modelId, nodesById, connections, 'Geometry');
        if (geomIds.length === 0) return;

        const geomNode = nodesById.get(geomIds[0]);
        const mesh = geomNode ? buildMeshFromGeometry(device, geomNode, convertAxis) : null;
        if (!mesh) return;

        const material = new StandardMaterial();
        const materialIds = findConnectedIds(modelId, nodesById, connections, 'Material');
        if (materialIds.length > 0) {
            const matNode = nodesById.get(materialIds[0]);
            const props70 = matNode?.node('Properties70');
            const diffuseProp = props70?.nodes('P', { 0: 'DiffuseColor' })[0];
            const r = diffuseProp?.prop(4, 'number') ?? 0.8;
            const g = diffuseProp?.prop(5, 'number') ?? 0.8;
            const b = diffuseProp?.prop(6, 'number') ?? 0.8;
            material.diffuse.set(r, g, b);
        } else {
            material.diffuse.set(0.8, 0.8, 0.8);
        }
        material.update();

        const meshInstance = new MeshInstance(mesh, material);
        const meshEntity = new Entity(`mesh_${modelIndex}`);
        meshEntity.addComponent('render', { meshInstances: [meshInstance] });

        // apply the Model node's local transform - FBX Vertices data is in object-local
        // space, so without this every imported mesh ends up stacked at the world origin
        const modelProps70 = modelNode.node('Properties70');
        const readVec3Prop = (name: string, fallback: Vec3): Vec3 => {
            const p = modelProps70?.nodes('P', { 0: name })[0];
            if (!p) return fallback;
            return new Vec3(p.prop(4, 'number') ?? fallback.x, p.prop(5, 'number') ?? fallback.y, p.prop(6, 'number') ?? fallback.z);
        };
        const localPos = convertAxis(readVec3Prop('Lcl Translation', Vec3.ZERO));
        const localEuler = readVec3Prop('Lcl Rotation', Vec3.ZERO);
        const localScale = readVec3Prop('Lcl Scaling', Vec3.ONE);

        meshEntity.setLocalPosition(localPos);
        // rotation applied directly (not axis-remapped) - correct for the common Y-up case;
        // Z-up sources may need this revisited
        meshEntity.setLocalRotation(new Quat().setFromEulerAngles(localEuler.x, localEuler.y, localEuler.z));
        meshEntity.setLocalScale(localScale);

        scratchRoot.addChild(meshEntity);
        meshCount++;
    });

    if (meshCount === 0) {
        scratchRoot.destroy();
        return false;
    }

    try {
        const exporter = new GltfExporter();
        const glb = await exporter.build(scratchRoot, {});
        const glbFile = new File([glb], filename.replace(/\.fbx$/i, '.glb'), { type: 'model/gltf-binary' });
        const model = await scene.assetLoader.loadModel(glbFile, filename);
        await scene.add(model);
        return true;
    } finally {
        scratchRoot.destroy();
    }
};

export { loadFbxMesh };
